import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { finalize, shareReplay, switchMap, catchError, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth';
import { currentSessionScope } from '../services/session-scope';
import { environment } from '../../../environments/environment';
const AUTH_ENDPOINTS = /\/auth\/(login|register|refresh|logout)(\?.*)?$/;
let refresh: { identity: string; result: Observable<boolean> } | null = null;
export function isApiRequest(url: string): boolean {
  const origin = typeof location === 'undefined' ? 'http://localhost' : location.origin;
  try {
    const api = new URL(environment.apiUrl, origin);
    const target = new URL(url, origin);
    return target.origin === api.origin && (target.pathname === api.pathname || target.pathname.startsWith(api.pathname + '/'));
  } catch { return false; }
}
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  if (!isApiRequest(req.url)) return next(req);
  const token = auth.getToken();
  const scope = currentSessionScope();
  if (req.headers.has('X-Offline-Scope') && req.headers.get('X-Offline-Scope') !== scope) {
    return throwError(() => new HttpErrorResponse({ status: 409, statusText: 'Session modifiée', url: req.url }));
  }
  return next(token ? withToken(req, token) : req).pipe(tap(() => {
    if (!AUTH_ENDPOINTS.test(req.url) && scope !== currentSessionScope()) throw new HttpErrorResponse({ status: 409, statusText: 'Session modifiée', url: req.url });
  }), catchError((error: HttpErrorResponse) => {
    if (error.status !== 401 || AUTH_ENDPOINTS.test(req.url) || scope !== currentSessionScope() || !auth.getRefreshToken()) return throwError(() => error);
    const identity = auth.getRefreshToken()!;
    if (!refresh || refresh.identity !== identity) {
      const active = { identity, result: undefined as unknown as Observable<boolean> };
      active.result = auth.refreshSession().pipe(finalize(() => { if (refresh === active) refresh = null; }), shareReplay({ bufferSize: 1, refCount: false }));
      refresh = active;
    }
    return refresh.result.pipe(switchMap(ok => {
      const renewedToken = auth.getToken();
      if (!ok || !renewedToken || scope !== currentSessionScope()) return throwError(() => error);
      return next(withToken(req, renewedToken)).pipe(tap(() => {
        if (scope !== currentSessionScope()) throw new HttpErrorResponse({ status: 409, statusText: 'Session modifiée', url: req.url });
      }));
    }));
  }));
};
function withToken(req: HttpRequest<unknown>, token: string) {
  return req.clone({ headers: req.headers.delete('X-Offline-Scope').set('Authorization', `Bearer ${token}`).set('ngsw-bypass', 'true') });
}
